import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import appDisplay from 'scoreboard/components/app-display'
import conductor from 'scoreboard/components/conductor'
import highlight from 'scoreboard/components/highlight'
import paste from 'scoreboard/components/paste/paste'
import ErrorView from 'scoreboard/components/error-view'
import projects from 'scoreboard/projects/projects'
import formHelper from 'scoreboard/components/form-helper'
import OrgPasteDestinationForm from './org-paste-destination-form'
import orgHelper from './org-helper'

/**
 * View to let the user pick a destination for a standard (non-template) org move (to new project) or copy.
 *
 * @param {Boolean} [options.copy=false] if this is a copy set to true
 * @param {Object} options.tree a reference to the org tree so that nodes can be updated
 */
var OrgMoveOrCopyDestinationForm = OrgPasteDestinationForm.extend({
	render: function () {
		OrgPasteDestinationForm.prototype.render.apply(this, arguments)
		// #31385 don't show naming panel for a move
		this.$('.org-name-panel').toggleClass('is-not-displayed', !this.options.copy)
		return this
	},

	title: function () {
		var key = this.options.copy ? 'common.copy.to' : 'common.move.to'
		return i18n.get(key, '<q>' + _.escape(this.model.get('label')) + '</q>')
	},

	primaryButtonLabel: function () {
		var key = this.options.copy ? 'common.copy' : 'common.move'
		return i18n.get(key)
	},

	/**
	 * Options for the copy (descendants or no descendants)
	 * Move (to new project) gets no option, descendants always go along for the ride
	 */
	copyOptions() {
		return this.options.copy ? OrgPasteDestinationForm.prototype.copyOptions.apply(this, arguments) : []
	},

	/**
	 * Preformatted banner message(s)
	 */
	preMessages: function () {
		var messages = []
		if (!this.options.copy) {
			const tree = this.options.tree
			// #57494 build a map of template ids to the ids of org nodes that
			// are template copies of them to optimize lookup of template sources
			const templateIdMap = this.buildTemplateIdMap(tree)
			const sourceOrgIds = this.getDescendantTemplateSources(this.options.tree, this.model.id, templateIdMap)
			if (this.model.isTemplate()) {
				sourceOrgIds.push(this.model.id)
			}
			if (!_.isEmpty(sourceOrgIds)) {
				// Moving an org with template copies
				const msgKey = this.model.isTemplate() ? 'common.project.move.warning.template' : 'common.project.move.warning.template.multiple'
				// use the templateIdMap to find the names of the template sources
				const copyOrgs =
					sourceOrgIds.flatMap(id => templateIdMap[id] || [])
				const listItems = _.reduce(copyOrgs, (html, orgId) => html += `<li>${tree.nodes[orgId].label}</li>`, '')
				messages.push(i18n.get(msgKey, highlight(this.model.get('label'))) + `<ul>${listItems}</ul>`)
			}
		}
		return messages
	},

	/**
	 * Builds a map of template org ids to the ids of orgs that are template
	 * copies of them.
	 */
	buildTemplateIdMap: function(tree) {
		return _.reduce(tree.nodes, (acc, node) => {
			if (node.templateId) {
				// initialize the array for this templateId if it doesn't exist yet
				if (!acc[node.templateId]) {
					acc[node.templateId] = []
				}
				// add the current org node id to the array of ids for this
				// templateId
				acc[node.templateId].push(node.id)
			}
			return acc
		}, {})
	},

	/**
	 * @param tree a kalpa-tree
	 * @param orgId The org to inspect the children of
	 * @param templateIdMap A map of template org ids to the ids of orgs that
	 * are template copies of them.
	 * @return {Number[]} orgIds that are template sources
	 */
	getDescendantTemplateSources(tree, orgId, templateIdMap) {
		const children = tree.children(orgId)
		return _.reduce(children, (acc, child) => {
			if (templateIdMap[child.id]) {
				acc.push(child.id)
			}
			// #57494 append recursive descendant source ids to the accumulator
			// so we are not just looking at the immediate children
			acc = acc.concat(this.getDescendantTemplateSources(tree, child.id, templateIdMap))
			return acc
		}, [])
	},

	/**
	 * Message displayed in right pane of org chooser
	 * "Move [org name] to [name of org selected on left side]"
	 */
	_orgMessage() {
		if (!this.options.copy) {
			return i18n.get('common.move.fromTo',
				this.model.get('label'), // Org being moved
				_.get(this.destinationOrg(), 'label')) // Target selection
		}
		return OrgPasteDestinationForm.prototype._orgMessage.apply(this, arguments)
	},

	submit: function () {
		const source = this.model.id
		const mode = this.options.copy ? 'copy' : 'move'
		const {id: destinationOrgId, projectId, newOrgRoot} = this.destinationOrg()

		const validationErrorHandler = (confirmXHR, xhr) => {
			const response = xhr.responseJSON
			if (response.triggersCopyDeletion) {
				// User tried to move a descendant of a template root, copies will be deleted
				const node = this.model
				const tree = this.options.tree
				const confirmationView = orgHelper.copyDeletionDialog({node, response, tree, confirmXHR})
				this.listenTo(confirmationView, 'done', this.trigger('done'))
			} else if (response.dependencies) {
				// A template copy is a descendant but it's root is outside the selected node's tree
				const orgLabel = highlight(this.model.get('label'))
				const message = `
						<span>${i18n.get('template.copyWithoutSource.immovable.copies', orgLabel)}</span>
						${orgHelper.htmlList(response.sources)}</br>
						<span>${i18n.get('template.copyWithoutSource.immovable.roots', orgLabel)}</span>
						${orgHelper.htmlList(response.dependencies)}`

				appDisplay.replaceDialog(new ErrorView({title: i18n.get('common.project.warning.immovable'), message}))
			} else {
				const rootExists = 'common.project.error.rootExists'
				if (xhr.responseJSON.messageCode === rootExists) {
					const label = projects.get(projectId).get('label')
					this.formView.errorMessage(i18n.get(rootExists, highlight(label)))
				} else {
					this.formView.error(xhr)
				}
			}
		}

		if (!this.options.copy && newOrgRoot) {
			var moveToRootOptions = confirmed => ({
				method: 'post',
				url: `api/organizations/${this.model.id}/move-to-root?${$.param({projectId, section: conductor.section()})}`,
				success: () => this.trigger('done')
			})
			const confirmXHR = () => $.ajax(moveToRootOptions(true))
			const promoteOptions = _.extend({}, moveToRootOptions(false), {error: validationErrorHandler.bind(this, confirmXHR)})
			$.ajax(promoteOptions)

		} else {
			// Move or Copy inside the same root org
			let commonPasteOptions = {
				section: 'organizations',
				id: this.model.id,
				projectId,
				mode,
				parentId: destinationOrgId,
				templated: false,
				entireTree: this.copyType() === 'tree'
			}

			if (this.copyType() === 'tree') {
				commonPasteOptions = _.extend(commonPasteOptions, {
					organizations: this.orgNamePanel.info()
				})
			}

			commonPasteOptions = _.extend(commonPasteOptions, formHelper.readFromDom(this.el))
			const confirmXHR = () => paste(_.extend({}, commonPasteOptions, {confirmed: true}))
			const pasteOpts = _.extend({}, commonPasteOptions, {error: validationErrorHandler.bind(this, confirmXHR)})
			paste(pasteOpts, () => {
				this.trigger('done')
			})
		}

	}
})

export default OrgMoveOrCopyDestinationForm

OrgMoveOrCopyDestinationForm.__filename__ = 'organizations/org-move-or-copy-destination-form.js'
